import { allocateMonthlyPlansToWeeksByMonthShare } from './PlannerRollup'

const getMonthPlanPercent = (m) => Number(m?.planPercent ?? m?.plan_percent ?? 0) || 0

export const getDominantEpochMonthForWeek = ({ ew, weekToMonthDayCounts, fallbackEpochMonth = null }) => {
  const monthMap = weekToMonthDayCounts?.get?.(Number(ew)) || weekToMonthDayCounts?.get?.(String(ew))
  if (!monthMap || !monthMap.size) return fallbackEpochMonth

  const sorted = Array.from(monthMap.entries()).sort((a, b) => {
    const dayDiff = Number(b?.[1] || 0) - Number(a?.[1] || 0)
    if (dayDiff !== 0) return dayDiff
    return Number(a?.[0] || 0) - Number(b?.[0] || 0)
  })

  return Number(sorted?.[0]?.[0] ?? fallbackEpochMonth ?? 0) || fallbackEpochMonth
}

export const seedWeeklyPlansFromMonthlyBase = ({
  epochWeeks,
  monthlyBaseUi,
  weekToMonthDayCounts,
  weeklyActualSalesByWeek,
}) => {
  const resolvedWeekToMonthDayCounts = (weekToMonthDayCounts && weekToMonthDayCounts.size > 0)
    ? weekToMonthDayCounts
    : new Map()

  const outByWeek = allocateMonthlyPlansToWeeksByMonthShare({
    monthlyBaseUi,
    weekToMonthDayCounts: resolvedWeekToMonthDayCounts,
  })

  const monthByEm = new Map()
  ;(monthlyBaseUi || []).forEach((m) => {
    const em = Number(m?.epochMonth)
    if (Number.isFinite(em)) monthByEm.set(em, m)
  })

  const firstMonth = Array.isArray(monthlyBaseUi) && monthlyBaseUi.length ? monthlyBaseUi[0] : null
  const firstEpochMonth = Number(firstMonth?.epochMonth ?? 0) || null
  const firstMonthSales = Number(firstMonth?.salesPlan ?? firstMonth?.sales_plan ?? firstMonth?.sales_monthly_plan ?? firstMonth?.salesMonthlyPlan ?? 0)
  const firstMonthMd = Number(firstMonth?.markdownPlan ?? firstMonth?.markdown_plan ?? firstMonth?.markdownMonthlyPlan ?? 0)
  const firstMonthPlanPercent = getMonthPlanPercent(firstMonth)

  const buildWeekRecord = ({ ew, salesPlan, markdownPlan, preferredPlanPercent, monthSalesTotal = 0, monthMarkdownTotal = 0 }) => {
    const weeklyActual = Number(weeklyActualSalesByWeek?.[Number(ew)] ?? weeklyActualSalesByWeek?.[String(ew)] ?? 0)
    const planPercent = Number(monthSalesTotal || 0) > 0
      ? (Number(salesPlan || 0) / Number(monthSalesTotal || 0)) * 100
      : weeklyActual
        ? (Number(salesPlan || 0) / weeklyActual) * 100
        : (Number(salesPlan || 0) > 0 ? Number(preferredPlanPercent || 0) : 0)
    const markdownPlanPercent = Number(monthMarkdownTotal || 0) > 0
      ? (Number(markdownPlan || 0) / Number(monthMarkdownTotal || 0)) * 100
      : 0

    return {
      ts: Date.now(),
      salesPlan: Number(salesPlan || 0),
      markdownPlan: Number(markdownPlan || 0),
      planPercent,
      markdownPlanPercent,
    }
  }

  Object.entries(outByWeek).forEach(([ew, rolledWeek]) => {
    const dominantEpochMonth = getDominantEpochMonthForWeek({
      ew,
      weekToMonthDayCounts: resolvedWeekToMonthDayCounts,
      fallbackEpochMonth: firstEpochMonth,
    })
    const dominantMonth = monthByEm.get(Number(dominantEpochMonth))
    const preferredPlanPercent = getMonthPlanPercent(dominantMonth)
    outByWeek[String(ew)] = buildWeekRecord({
      ew,
      salesPlan: Number(rolledWeek?.salesPlan ?? 0),
      markdownPlan: Number(rolledWeek?.markdownPlan ?? 0),
      preferredPlanPercent,
      monthSalesTotal: Number(dominantMonth?.salesPlan ?? dominantMonth?.sales_plan ?? dominantMonth?.sales_monthly_plan ?? dominantMonth?.salesMonthlyPlan ?? 0),
      monthMarkdownTotal: Number(dominantMonth?.markdownPlan ?? dominantMonth?.markdown_plan ?? dominantMonth?.markdownMonthlyPlan ?? 0),
    })
  })

  if (Array.isArray(epochWeeks) && epochWeeks.length && firstMonth) {
    const seededWeekCount = Math.min(4, epochWeeks.length)
    const firstWindowWeeks = epochWeeks.slice(0, seededWeekCount)
    const overlapWeeks = epochWeeks.filter((ew) => Number(outByWeek[String(ew)]?.salesPlan ?? 0) > 0)
    const firstWeekMissing = firstWindowWeeks.length
      ? Number(outByWeek[String(firstWindowWeeks[0])]?.salesPlan ?? 0) === 0
      : false
    const laterWindowHasSeed = firstWindowWeeks.slice(1).some((ew) => Number(outByWeek[String(ew)]?.salesPlan ?? 0) > 0)
    const shouldEvenSeedFirstMonth = overlapWeeks.length <= 1 || (firstWeekMissing && laterWindowHasSeed)

    if (shouldEvenSeedFirstMonth) {
      const perWeekSales = seededWeekCount ? (firstMonthSales / seededWeekCount) : 0
      const perWeekMd = seededWeekCount ? (firstMonthMd / seededWeekCount) : 0

      firstWindowWeeks.forEach((ew) => {
        outByWeek[String(ew)] = buildWeekRecord({
          ew,
          salesPlan: perWeekSales,
          markdownPlan: perWeekMd,
          preferredPlanPercent: firstMonthPlanPercent,
          monthSalesTotal: firstMonthSales,
          monthMarkdownTotal: firstMonthMd,
        })
      })

      epochWeeks.forEach((ew, idx) => {
        if (idx >= seededWeekCount && !outByWeek[String(ew)]) {
          outByWeek[String(ew)] = buildWeekRecord({
            ew,
            salesPlan: 0,
            markdownPlan: 0,
            preferredPlanPercent: 0,
            monthSalesTotal: 0,
            monthMarkdownTotal: 0,
          })
        }
      })
    }
  }

  return outByWeek
}

